import { toQueryStringParam } from './array';
import { formatDateByDayMonth } from './date';
import { formatAsBRLWithSignal } from './number';
import { get } from './request';

import APP from 'constants/app';
import COMPARAI from 'constants/comparai';
import OFFER from 'constants/offer';
import WISHLIST from 'constants/wishlist';

const OFFERS_CLIENT_LIMIT = 36;
const VALID_APPROVED_OFFER_STATUSES = [
  OFFER.STATUSES.PRE_APPROVED,
  OFFER.STATUSES.TOP_OFFER,
  OFFER.STATUSES.APPROVED,
  OFFER.STATUSES.HIDDEN,
  OFFER.STATUSES.RESTRICTED,
  OFFER.STATUSES.ETERNAL,
  OFFER.STATUSES.SCHEDULED,
];

export const addLikeToOffer = ({ action, offer, offerId }) => {
  const newOffer = { ...offer };

  if (newOffer.offerId === offerId) {
    if (action === OFFER.ACTIONS.LIKE) {
      newOffer.offerLikes += 1;
      newOffer.isLiked = true;
    }

    if (action === OFFER.ACTIONS.REMOVE) {
      newOffer.offerLikes -= 1;
      newOffer.isLiked = false;
    }

    return newOffer;
  }

  return newOffer;
};

export const formatOfferPageDescription = ({
  storeName,
  storeUrl,
  offerPublishedDate,
  offerPrice,
  offerTitle,
}) => {
  const currStoreName = storeName || storeUrl;

  return `Promoção do dia ${formatDateByDayMonth(
    offerPublishedDate
  )}: ${offerTitle} por R$ ${Math.round(
    offerPrice
  )} em ${currStoreName}. Aproveite essa e outras ofertas já com cupom de desconto!`;
};

export const formatOfferPageTitle = (offerTitle, offerPrice) =>
  `${offerTitle.substring(0, 30)}... R$ ${Math.round(offerPrice)} - ${
    APP.NAME
  }`;

export const formatOfferWishlistDescription = ({
  keywordMin: min,
  keywordMax: max,
  keywordTop,
}) => {
  const [maxPriceInBrl, minPriceInBrl] = [max, min].map((value) =>
    formatAsBRLWithSignal(value)
  );
  const getFormattedOfferType = () => {
    const isFreeOffersAlertOnly = min === 0 && max === 0;

    if (isFreeOffersAlertOnly) {
      return 'Apenas ofertas grátis';
    }

    if (keywordTop) {
      return WISHLIST.OFFER.DESCRIPTIONS.ONLY_LOWEST_PRICE_OFFERS;
    }

    return WISHLIST.OFFER.DESCRIPTIONS.ALL_OFFERS;
  };

  let formattedPriceRange;

  if (min > 0 && min < max) {
    formattedPriceRange = `De ${minPriceInBrl} até ${maxPriceInBrl}`;
  } else if (max > 0) {
    formattedPriceRange = `Até ${maxPriceInBrl}`;
  } else if (min > 0) {
    formattedPriceRange = `A partir de ${minPriceInBrl}`;
  }

  return formattedPriceRange
    ? `${formattedPriceRange}\n${getFormattedOfferType()}`
    : getFormattedOfferType();
};

const getCategoryIdsQs = (categories) =>
  toQueryStringParam(categories.map(({ categoryId }) => categoryId));

export const getCategoryOffersQueryFn = async ({
  after = false,
  endpoint,
  offersFilters,
  offersSort,
  limit = OFFERS_CLIENT_LIMIT,
}) => {
  let offersEndpointURL = limit
    ? `${endpoint}/offers?limit=${limit}&sort=${offersSort}`
    : `${endpoint}/offers?sort=${offersSort}`;

  if (offersFilters?.stores.length > 0) {
    const storesIds = offersFilters.stores.map(({ storeId }) => storeId);
    offersEndpointURL = `${offersEndpointURL}&stores=${toQueryStringParam(
      storesIds
    )}`;
  }

  if (offersFilters?.priceRange.min > 0) {
    offersEndpointURL = `${offersEndpointURL}&min=${offersFilters?.priceRange.min}`;
  }

  if (offersFilters?.priceRange.max > 0) {
    offersEndpointURL = `${offersEndpointURL}&max=${offersFilters?.priceRange.max}`;
  }

  if (offersFilters?.venue) {
    offersEndpointURL = `${offersEndpointURL}&venue=${offersFilters?.venue}`;
  }

  if (after) {
    return get(`${offersEndpointURL}&after=${after}`);
  }

  return get(offersEndpointURL);
};

const getSubcategoryIdsQs = (subcategories) =>
  toQueryStringParam(subcategories.map(({ subcategoryId }) => subcategoryId));

export const getOfferPageEventCategory = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.FINISHED ? 'active' : 'finished';

export const getOffersQueryCacheConfigWithServerSideFetching = ({
  isClientSide = false,
  initialOffers,
  cacheTime = 100,
  staleTime = 0,
  refetchOnWindowFocus = true,
  onGetFetchMore,
  ...queryOptions
}) => {
  const config = {
    cacheTime,
    staleTime,
    refetchOnWindowFocus,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    getFetchMore: (lastOffers) => {
      onGetFetchMore(lastOffers);
      return lastOffers?.after;
    },
    queryFnParamsFilter: () => [],
    ...queryOptions,
  };

  if (!isClientSide) {
    config.initialData = () => [initialOffers];
  }

  return config;
};

export const getOffersQueryFn = async ({
  after = false,
  before = false,
  formatAsPage = false,
  offersSort = 'latest',
  offersFilters,
  offersEndpointBase = '/offers',
  limit = OFFERS_CLIENT_LIMIT,
}) => {
  let offersEndpointURL = limit
    ? `${offersEndpointBase}?limit=${limit}&sort=${offersSort}`
    : `${offersEndpointBase}?sort=${offersSort}`;

  if (offersFilters?.categories?.length > 0) {
    const categoriesIdsQs = getCategoryIdsQs(offersFilters.categories);
    offersEndpointURL = `${offersEndpointURL}&categories_excluded=${categoriesIdsQs}`;
  }

  if (offersFilters?.subcategories?.length > 0) {
    const subcategoriesIdsQs = getSubcategoryIdsQs(offersFilters.subcategories);
    offersEndpointURL = `${offersEndpointURL}&subcategories_excluded=${subcategoriesIdsQs}`;
  }

  if (
    offersFilters &&
    offersFilters.isInternationalOffersActive !== undefined &&
    !offersFilters.isInternationalOffersActive
  ) {
    offersEndpointURL = `${offersEndpointURL}&only_national=1`;
  }

  if (before) {
    return get(`${offersEndpointURL}&before=${before}`);
  }

  if (after) {
    return get(`${offersEndpointURL}&after=${after}`);
  }

  return formatAsPage ? [await get(offersEndpointURL)] : get(offersEndpointURL);
};

export const hasBadge = (offerIsHighlight, offerStatusName) =>
  hasVerifiedBadge(offerStatusName) ||
  hasHighlightBadge(offerIsHighlight, offerStatusName) ||
  offerStatusName === OFFER.STATUSES.TOP_OFFER ||
  offerStatusName === OFFER.STATUSES.FINISHED;

export const hasHighlightBadge = (offerIsHighlight, offerStatusName) =>
  offerIsHighlight && offerStatusName !== OFFER.STATUSES.FINISHED;

export const hasVerifiedBadge = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.WAITING &&
  offerStatusName !== OFFER.STATUSES.PRE_APPROVED &&
  offerStatusName !== OFFER.STATUSES.FINISHED &&
  offerStatusName !== OFFER.STATUSES.TOP_OFFER;

export const isAnyOfferActive = (offers) =>
  offers.find(
    ({ offerStatusName }) => offerStatusName !== OFFER.STATUSES.FINISHED
  );

export const isComparaiPartnership = (partner) => partner === COMPARAI.PARTNER;

export const isInternationalOfferByOfferTags = (offerTags) =>
  offerTags.some(({ name }) => name === 'Internacional');

export const isOfferCategoryIdFilterActive = (categories, categoryId) =>
  categories
    .map(({ categoryId: filteredCategoryId }) => filteredCategoryId)
    .some((filteredCategoryId) => filteredCategoryId === categoryId);

export const isOfferLiked = (offer, ratings) =>
  ratings?.some((offerId) => offerId === offer.offerId);

export const isOfferSubcategoryIdFilterActive = (
  subcategories,
  subcategoryId
) =>
  subcategories
    .map(({ subcategoryId: filteredSubcategoryId }) => filteredSubcategoryId)
    .some((filteredSubcategoryId) => filteredSubcategoryId === subcategoryId);

export const isValidApprovedOffer = (offerStatusName) =>
  VALID_APPROVED_OFFER_STATUSES.includes(offerStatusName);

export const toFormattedOfferCount = (length) => {
  if (length === 0 || !length) return OFFER.COUNT_FORMAT.NO_OFFERS;
  if (length === 1) return OFFER.COUNT_FORMAT.ONE_OFFER;

  return `${length} ${OFFER.COUNT_FORMAT.OFFERS}`;
};

export const toFormattedOfferOldPrice = (price, priceType = null) => {
  if (!price) {
    return null;
  }

  if (priceType === OFFER.PRICE_TYPES.STARTING_AT) {
    return 'A partir de';
  }

  return price;
};

export const toFormattedOfferPrice = (price, priceType) => {
  if (priceType === OFFER.PRICE_TYPES.COUPON) return 'Cupom';
  if (priceType === OFFER.PRICE_TYPES.FREE) return 'de graça!';

  return price;
};

export const toFormattedNewOffersText = (offersCount) =>
  offersCount === 1
    ? `Atualize para ver ${offersCount} nova oferta`
    : `Atualize para ver ${offersCount} novas ofertas`;

export const toFormattedNewOffersCount = (offersLength) =>
  offersLength > 99 ? '99+' : offersLength;

export const toSidebarFeaturedOffers = (recommendedOffers, featuredOffers) => {
  if (!recommendedOffers && !featuredOffers) {
    return [];
  }

  if (recommendedOffers.length < OFFER.SIDEBAR_FEATURED_OFFERS_LIMIT) {
    let newFeaturedOffersArray = [];
    newFeaturedOffersArray = [...recommendedOffers, ...featuredOffers];
    return newFeaturedOffersArray.slice(0, OFFER.SIDEBAR_FEATURED_OFFERS_LIMIT);
  }

  return recommendedOffers;
};
